import { SUBSCRIPTION_TIERS, type SubscriptionTier } from '@/lib/api/subscriptions';

/**
 * localStorage keys used during the subscription flow.
 * These are cleared on plan selection page mount to prevent data leaking between sessions.
 */
export const SUBSCRIPTION_STORAGE_KEYS = {
    PRICE_ID: 'subscriptionPriceId',
    CREDIT_CARD_REQUIRED: 'creditCardRequired',
    TRIAL_DAYS: 'trialDays',
    PROMO_ID: 'promoId',
} as const;

/** Array of all subscription storage keys for bulk operations */
export const ALL_SUBSCRIPTION_STORAGE_KEYS = Object.values(SUBSCRIPTION_STORAGE_KEYS);

/**
 * Subscription status constants
 * Consolidated from multiple locations - this is the single source of truth
 */
export const SUBSCRIPTION_STATUS = {
    CANCELLED: 0,
    ACTIVE: 1,
    PENDING_ASSISTANCE: 2,
    DOWNGRADED: 3,
    UPGRADED: 4,
} as const;

/**
 * Plan feature structure matching original Vue design
 */
export interface PlanFeature {
    icon: string;
    header: string;
    description: string;
    isPro?: boolean;
}

/**
 * Common features available in both Creator and Creator Pro
 */
export const COMMON_FEATURES: PlanFeature[] = [
    {
        icon: '📱',
        header: 'Mobile Optimized Link-in-Bio Store',
        description: 'Replaces Linktree, Squarespace',
    },
    {
        icon: '🗓️',
        header: 'Calendar Bookings',
        description: 'Replaces Calendly',
    },
    {
        icon: '🎓',
        header: 'Course Builder',
        description: 'Replaces Kajabi, Teachable',
    },
];

/**
 * Creator Pro-only features
 */
export const PRO_FEATURES: PlanFeature[] = [
    {
        icon: '✉️',
        header: 'Unlimited Email Marketing',
        description: 'Save $100s vs. Mailchimp',
        isPro: true,
    },
    {
        icon: '📊',
        header: 'Order Bumps',
        description: 'Increase your sales by 40%+',
        isPro: true,
    },
    {
        icon: '💸',
        header: 'Payment Plans',
        description: 'Increase your conversion rates by 20%+',
        isPro: true,
    },
];

/**
 * Get features for a specific tier
 */
export function getPlanFeatures(tier: SubscriptionTier): PlanFeature[] {
    if (tier === SUBSCRIPTION_TIERS.CREATOR_PRO) {
        return [...COMMON_FEATURES, ...PRO_FEATURES];
    }
    return COMMON_FEATURES;
}

/**
 * Plan display names
 */
export const PLAN_NAMES: Record<SubscriptionTier, string> = {
    [SUBSCRIPTION_TIERS.CREATOR]: 'Creator',
    [SUBSCRIPTION_TIERS.CREATOR_PRO]: 'Creator Pro',
    [SUBSCRIPTION_TIERS.AFFILIATE]: 'Affiliate',
};

/**
 * Change type labels for button text
 */
export const CHANGE_TYPE_LABELS = {
    UPGRADE: 'Upgrade',
    DOWNGRADE: 'Downgrade',
    SWITCH_TO_MONTHLY: 'Switch to Monthly',
    SWITCH_TO_YEARLY: 'Switch to Yearly',
    RENEW: 'Renew',
} as const;
